import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { CollaborativeApps } from 'src/app/sessions/session/iframe/additional-apps.utils';
import { ItemModel } from 'src/app/models/itemModel';
import { BoardItemsManager } from 'src/app/sessions/session/items-canvas/board-items/board-items-manager';
import { AppsInsertionService } from 'src/app/sessions/session/items-canvas/board-items/apps-insertion.service';
import { SlidesImportUrlDialogComponent } from 'src/app/dialogs/slides-import/slides-import-url-dialog/slides-import-url-dialog.component';
import { SlidesImportModeDialogComponent } from 'src/app/dialogs/slides-import/slides-import-mode-dialog/slides-import-mode-dialog.component';
import {
  SlidesImportAccessDialogComponent,
  SlidesImportAccessDialogData,
} from 'src/app/dialogs/slides-import/slides-import-access-dialog/slides-import-access-dialog.component';
import {
  SlidesImportCheckingAccessDialogComponent,
  SlidesImportCheckingAccessDialogData,
  SlidesImportDialogMode,
} from 'src/app/dialogs/slides-import/slides-import-checking-access-dialog/slides-import-checking-access-dialog.component';
import { SlidesImportMode, SlidesAccess } from 'src/app/models/slides-import.models';
import { validateGoogleSlidesUrl } from 'src/app/services/slides-import/slides-url.validator';
import { TranslateService } from '@ngx-translate/core';

export interface SlidesImportFlowOptions {
  addToNewBoard?: boolean;
  isPrivate?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SlidesImportFlowService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly appsInsertionService: AppsInsertionService,
    private readonly translate: TranslateService,
    private readonly zone: NgZone,
  ) {}

  async openGoogleAppsDialog(options: SlidesImportFlowOptions = {}): Promise<void> {
    const { addToNewBoard = false, isPrivate = false } = options;

    const urlResult = await this.openUrlDialog();
    if (!urlResult?.url) {
      return;
    }

    await this.openModeDialogAndContinue(
      urlResult.url,
      urlResult.presentationId,
      addToNewBoard,
      isPrivate,
    );
  }

  async openFlowWithPastedUrl(
    pastedUrl: string,
    options: SlidesImportFlowOptions = {},
  ): Promise<void> {
    const { addToNewBoard = false, isPrivate = false } = options;

    const { valid, normalizedUrl, presentationId } = validateGoogleSlidesUrl(pastedUrl);
    if (!valid || !normalizedUrl || !presentationId) {
      return;
    }

    // Needs to run inside zone to render correctly as the paste is out of angular zone,
    // if it was rendered outside zone, it will take time in a blank UI until it renders correctly
    this.zone.run(() => {
      this.openModeDialogAndContinue(normalizedUrl, presentationId, addToNewBoard, isPrivate);
    });
  }

  private async openModeDialogAndContinue(
    url: string,
    presentationId: string,
    addToNewBoard: boolean,
    isPrivate: boolean,
  ): Promise<void> {
    const modeResult = await this.openModeDialog();
    if (!modeResult?.mode) {
      return;
    }

    if (modeResult.mode === SlidesImportMode.Collaborate) {
      this.insertIframeAndComplete(url, addToNewBoard, isPrivate);
      return;
    }

    await this.handlePresentPath(presentationId, addToNewBoard, isPrivate);
  }

  private openUrlDialog(): Promise<{ url: string; presentationId: string } | null> {
    return firstValueFrom(
      this.dialog
        .open(SlidesImportUrlDialogComponent, {
          disableClose: false,
          panelClass: 'slides-import-url-dialog',
          width: '320px',
        })
        .afterClosed(),
    );
  }

  private openModeDialog(): Promise<{ mode: SlidesImportMode } | null> {
    return firstValueFrom(
      this.dialog
        .open(SlidesImportModeDialogComponent, {
          disableClose: false,
          panelClass: 'slides-import-mode-dialog',
          width: '700px',
        })
        .afterClosed(),
    );
  }

  private async insertWebViewerAndComplete(
    url: string,
    addToNewBoard: boolean,
    isPrivate: boolean,
  ): Promise<void> {
    const loadingDialogRef = this.dialog.open(SlidesImportCheckingAccessDialogComponent, {
      data: {
        title: this.translate.instant('Import Google Slides'),
        message: this.translate.instant('Loading your presentation...'),
        mode: SlidesImportDialogMode.Loading,
      },
      disableClose: false,
      panelClass: 'slides-import-checking-access-dialog',
      width: '700px',
    });

    try {
      await this.appsInsertionService.createAndInsertWebViewer({
        defaultUrl: url,
        appInstanceId: CollaborativeApps.GOOGLE_SLIDES,
        isPrivate,
        addToNewBoard,
      });
    } finally {
      loadingDialogRef.close();
    }
  }

  private async handlePresentPath(
    presentationId: string,
    addToNewBoard: boolean,
    isPrivate: boolean,
  ): Promise<void> {
    // we first check the access to the presentation
    // if the access is public,
    //  the dialog updates itself to show the loading message and the web viewer is inserted
    //  we close the dialog once that is done
    // if the access is restricted,
    //  we close the dialog and  show the access dialog to request to change the access to public or log in instead

    const dialogRef = this.dialog.open<
      SlidesImportCheckingAccessDialogComponent,
      SlidesImportCheckingAccessDialogData,
      SlidesAccess | false
    >(SlidesImportCheckingAccessDialogComponent, {
      disableClose: false,
      data: {
        title: this.translate.instant('How would you like to use Google Slides?'),
        message: this.translate.instant('Checking slides access...'),
        mode: SlidesImportDialogMode.Checking,
        presentationId,
        loadingTitle: this.translate.instant('How would you like to use Google Slides?'),
        loadingMessage: this.translate.instant('Loading your presentation...'),
      },
      panelClass: 'slides-import-checking-access-dialog',
      width: '700px',
    });

    const access = await dialogRef.componentInstance.whenAccessChecked;

    if (access === false) {
      return;
    }

    const slideshowUrl = `https://docs.google.com/presentation/d/${presentationId}/embed?start=false&loop=false&delayms=3000`;

    if (access === SlidesAccess.Public) {
      try {
        await this.appsInsertionService.createAndInsertWebViewer({
          defaultUrl: slideshowUrl,
          appInstanceId: CollaborativeApps.GOOGLE_SLIDES,
          isPrivate,
          addToNewBoard,
        });
      } finally {
        dialogRef.close(SlidesAccess.Public);
      }
      return;
    }

    // if the access is restricted, we show the access dialog to request to change the access to public or log in instead
    const proceeded = await firstValueFrom(
      this.dialog
        .open<SlidesImportAccessDialogComponent, SlidesImportAccessDialogData>(
          SlidesImportAccessDialogComponent,
          {
            disableClose: false,
            data: { presentationId },
            panelClass: 'slides-import-access-dialog',
            width: '450px',
          },
        )
        .afterClosed(),
    );

    if (proceeded) {
      await this.insertWebViewerAndComplete(slideshowUrl, addToNewBoard, isPrivate);
    }
  }

  private insertIframeAndComplete(url: string, addToNewBoard: boolean, isPrivate: boolean): void {
    this.appsInsertionService.beforeAppInsertion(
      ItemModel.IFrame,
      CollaborativeApps.GOOGLE_SLIDES,
      addToNewBoard,
    );
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.IFrame>(
      ItemModel.IFrame,
      {
        appInstanceId: CollaborativeApps.GOOGLE_SLIDES,
        url,
        isPrivate,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }
}
